import { useEffect } from 'react';
import { create } from 'zustand';

/**
 * The prospect journey's language: Hebrew first, Arabic and English beside it
 * (the front door, the tour pages, and a prospect's home with its tour card).
 * The rest of the portal (registration, the lease) is Hebrew.
 *
 * The language is, in order: `?lang=he|ar|en` on the URL (an ad links straight
 * to Arabic: `/o/<handle>?lang=ar`; kept from then on), the choice made with
 * the switch on this device (localStorage), the browser's language (Arabic for
 * ar*), else Hebrew.
 */
export type PortalLang = 'he' | 'ar' | 'en';
export const PORTAL_LANGS: readonly PortalLang[] = ['he', 'ar', 'en'];

export const LANG_KEY = 'portal_lang';

/** Each language named in itself, for the switch. */
export const LANG_LABEL: Record<PortalLang, string> = { he: 'עברית', ar: 'العربية', en: 'English' };

/** The app's name: "TendedLoop · <resident portal>" in each language. */
export const PORTAL_NAME: Record<PortalLang, string> = {
  he: 'פורטל הדיירים',
  ar: 'بوابة السكان',
  en: 'Resident portal',
};

export const dirOf = (lang: PortalLang): 'rtl' | 'ltr' => (lang === 'en' ? 'ltr' : 'rtl');

/**
 * The Intl locale of each language. Arabic as written in Israel, with Latin
 * digits (16:00, 5 نوفمبر): the digits on the office's signs and in its emails.
 */
export const LOCALE: Record<PortalLang, string> = { he: 'he-IL', ar: 'ar-IL-u-nu-latn', en: 'en-GB' };

export function asLang(v: unknown): PortalLang | null {
  return v === 'he' || v === 'ar' || v === 'en' ? v : null;
}

function readStored(): PortalLang | null {
  try {
    return asLang(localStorage.getItem(LANG_KEY));
  } catch {
    return null; // storage unavailable: the browser's language decides
  }
}

function store(lang: PortalLang): void {
  try {
    localStorage.setItem(LANG_KEY, lang);
  } catch {
    /* storage unavailable: the choice holds for this visit */
  }
}

/** Arabic for a browser set to Arabic (ar, ar-IL, ar-EG...), else Hebrew. */
export function browserLang(languages: readonly string[] | undefined): PortalLang {
  for (const l of languages ?? []) {
    const base = l.toLowerCase().split(/[-_]/)[0];
    if (base === 'ar') return 'ar';
    if (base === 'he' || base === 'iw') return 'he';
  }
  return 'he';
}

/**
 * The language for a page opened with this URL: `?lang=`, or a tour email's
 * `#l=` (also remembered), else the stored choice, else the browser's.
 */
export function initialLang(search: string, languages?: readonly string[], hash = ''): PortalLang {
  const fromUrl =
    asLang(new URLSearchParams(search).get('lang')) ?? asLang(new URLSearchParams(hash.replace(/^#/, '')).get('l'));
  if (fromUrl) {
    store(fromUrl);
    return fromUrl;
  }
  return readStored() ?? browserLang(languages);
}

interface LangState {
  lang: PortalLang;
  /** The switch: change the language and remember it on this device. */
  setLang: (lang: PortalLang) => void;
}

const isBrowser = typeof window !== 'undefined';

export const useLang = create<LangState>((set) => ({
  lang: isBrowser
    ? initialLang(window.location.search, navigator.languages ?? [navigator.language], window.location.hash)
    : 'he',
  setLang: (lang) => {
    store(lang);
    set({ lang });
  },
}));

/** Take a language the page was opened with (`?lang=`, a link's `#l=`): it becomes the choice. */
export function adoptLang(raw: unknown): void {
  const lang = asLang(raw);
  if (lang && lang !== useLang.getState().lang) useLang.getState().setLang(lang);
}

/**
 * The page's language and direction on <html> (screen readers, the browser's
 * own controls, text direction), and the tab's title. Hebrew again on leaving.
 */
export function useDocumentLang(lang: PortalLang, title?: string): void {
  useEffect(() => {
    const html = document.documentElement;
    html.lang = lang;
    html.dir = dirOf(lang);
    return () => {
      html.lang = 'he';
      html.dir = 'rtl';
    };
  }, [lang]);
  useEffect(() => {
    const prev = document.title;
    document.title = title ?? `TendedLoop · ${PORTAL_NAME[lang]}`;
    return () => {
      document.title = prev;
    };
  }, [lang, title]);
}

/** Arabic counts: "ساعتين", "3 ساعات", "24 ساعة" (and the same for seconds). */
export function arCount(n: number, one: string, two: string, few: string, many: string): string {
  if (n === 1) return one;
  if (n === 2) return two;
  return n >= 3 && n <= 10 ? `${n} ${few}` : `${n} ${many}`;
}
